import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardBody, CardFooter, CardHeader, CardTitle } from "@hp/ui";
import type { AlertSettings } from "@hp/api-client";
import { updateAlertSettings } from "../api/alertSettings";
import { ErrorBlock } from "./QueryStatus";

/** What the dashboard home shows. Org-wide: it applies to everyone on the team. */
export function DashboardDisplayForm({ settings }: { settings: AlertSettings }) {
  const queryClient = useQueryClient();
  const [showTrueUp, setShowTrueUp] = React.useState(settings.show_true_up_gaps);
  const [showSearch, setShowSearch] = React.useState(settings.show_event_search);
  const [showReviewers, setShowReviewers] = React.useState(settings.show_top_reviewers);
  const [showConfidence, setShowConfidence] = React.useState(settings.show_confidence_info);
  const [regulationHome, setRegulationHome] = React.useState(settings.regulation_home);
  const [hideNoHpOrder, setHideNoHpOrder] = React.useState(settings.hide_no_hp_order_events ?? true);

  React.useEffect(() => setShowTrueUp(settings.show_true_up_gaps), [settings.show_true_up_gaps]);
  React.useEffect(() => setShowSearch(settings.show_event_search), [settings.show_event_search]);
  React.useEffect(() => setShowReviewers(settings.show_top_reviewers), [settings.show_top_reviewers]);
  React.useEffect(() => setShowConfidence(settings.show_confidence_info), [settings.show_confidence_info]);
  React.useEffect(() => setRegulationHome(settings.regulation_home), [settings.regulation_home]);
  React.useEffect(() => setHideNoHpOrder(settings.hide_no_hp_order_events ?? true), [settings.hide_no_hp_order_events]);

  const mutation = useMutation({
    mutationFn: updateAlertSettings,
    onSuccess: (updated) => {
      queryClient.setQueryData(["alert-settings"], updated);
      void queryClient.invalidateQueries({ queryKey: ["cil"] }); // the lists and counts change with this setting
    },
  });

  function handleSave(event: React.FormEvent) {
    event.preventDefault();
    mutation.mutate({ show_true_up_gaps: showTrueUp, show_event_search: showSearch, show_top_reviewers: showReviewers, show_confidence_info: showConfidence, regulation_home: regulationHome, hide_no_hp_order_events: hideNoHpOrder });
  }

  return (
    <form onSubmit={handleSave}>
      <Card>
        <CardHeader>
          <CardTitle>Dashboard home</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={regulationHome}
              onChange={(event) => setRegulationHome(event.target.checked)}
            />
            <span>
              <span className="font-medium">Make HP Genie the home page</span>
              <span className="block text-xs text-zinc-500">
                Ticked: the HP Genie chat opens first ("Overview" stays in the sidebar). Unticked: Overview is the home page again. The floating HP Genie button at the bottom right is hidden either way.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={showTrueUp}
              onChange={(event) => setShowTrueUp(event.target.checked)}
            />
            <span>
              <span className="font-medium">Show True-up gaps</span>
              <span className="block text-xs text-zinc-500">
                Ticked: the card appears on the home page with the number of items HP was allowed less than it claimed. Unticked: it is removed from the home page (the True-up gaps page still opens from its link).
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={showSearch}
              onChange={(event) => setShowSearch(event.target.checked)}
            />
            <span>
              <span className="font-medium">Show search and sort in the changed-events popup</span>
              <span className="block text-xs text-zinc-500">
                Ticked: a search box and a "Sort by" menu appear on the right of the "Changed events from Disallowed / Allowed" popups. Unticked: the popups show the list only.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={showReviewers}
              onChange={(event) => setShowReviewers(event.target.checked)}
            />
            <span>
              <span className="font-medium">Show Top reviewers</span>
              <span className="block text-xs text-zinc-500">
                Ticked: a card on the home page lists the people who did the most review work: events reviewed, confirmed and rejected, and review items closed. Unticked: it is removed from the home page.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={showConfidence}
              onChange={(event) => setShowConfidence(event.target.checked)}
            />
            <span>
              <span className="font-medium">Show confidence score information</span>
              <span className="block text-xs text-zinc-500">
                Ticked: on changed events, the info button next to the confidence score (how it was worked out), the cap note, and each ruling's own "Reading confidence" bar are shown. Unticked: only the overall score is shown.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-zinc-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-600"
              checked={hideNoHpOrder}
              onChange={(event) => setHideNoHpOrder(event.target.checked)}
            />
            <span>
              <span className="font-medium">Hide "No HP order on this cost" events</span>
              <span className="block text-xs text-zinc-500">
                Ticked: events where another party won a cost HP has no order on ("Possible new claim, if HP bears the cost") are left out of the Overview, every changed-events list, the counts and the alerts. Unticked: they are shown with the others.
              </span>
            </span>
          </label>
        </CardBody>
        <CardFooter className="flex flex-wrap items-center justify-between gap-3">
          <Button type="submit" disabled={mutation.isPending || (showTrueUp === settings.show_true_up_gaps && showSearch === settings.show_event_search && showReviewers === settings.show_top_reviewers && showConfidence === settings.show_confidence_info && regulationHome === settings.regulation_home && hideNoHpOrder === (settings.hide_no_hp_order_events ?? true))}>
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
          <span className="text-xs text-zinc-500">
            {mutation.isSuccess ? "Saved. " : ""}Applies to everyone on the team. Only a reviewer or admin can change it.
          </span>
        </CardFooter>
        {mutation.isError ? <ErrorBlock message="Could not save. Only a reviewer or admin can change this." surface="canvas" /> : null}
      </Card>
    </form>
  );
}
